import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { attendanceActions } from "../../store/AttendanceSlice";
import { differenceInHours, isSameDay, parse } from "date-fns";
import { getTimeDifference } from "../../utils/time";
import LoginIcon from "../../assets/icons/login.svg";
import LogoutIcon from "../../assets/icons/logout.svg";
import AttachIcon from "../../assets/icons/attach_file.svg";
import DeleteIcon from "../../assets/icons/delete.svg";
import "./Regularisation.css";

const Regularisation = ({
  onSubmitRegularisation,
  minDate,
  maxDate,
  resetFlag,
  regularisationFileUploadResponseStatus,
  submitRegularisationResponseStatus,
}) => {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  const today = new Date();

  const normalizeStart = (date) =>
    date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;

  const normalizeEnd = (date) =>
    date
      ? new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          23,
          59,
          59,
        )
      : null;

  const todayStart = normalizeStart(new Date());

  const threeDaysAgo = new Date(todayStart);
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

  const min = normalizeStart(
    minDate && minDate > threeDaysAgo ? minDate : threeDaysAgo,
  );

  const yesterday = new Date(todayStart);
  yesterday.setDate(yesterday.getDate() - 1);

  const max = normalizeEnd(
    maxDate && maxDate < yesterday ? maxDate : yesterday,
  );

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = [];
  for (let y = 2000; y <= 2035; y++) years.push(y);

  const [cal1, setCal1] = useState({
    month: today.getMonth(),
    year: today.getFullYear(),
    selected: null,
  });

  const [cal2, setCal2] = useState({
    month: today.getMonth(),
    year: today.getFullYear(),
    selected: null,
  });

  const isPunchInSelected = !!cal1?.selected;

  const punchInDateObj = cal1?.selected
    ? new Date(cal1.year, cal1.month, cal1.selected)
    : null;

  const cal2Min = punchInDateObj;

  const cal2Max = punchInDateObj
    ? new Date(
        punchInDateObj.getFullYear(),
        punchInDateObj.getMonth(),
        punchInDateObj.getDate() + 1,
      )
    : null;

  const [reason, setReason] = useState("");
  const [openReasonDrop, setOpenReasonDrop] = useState(false);
  const [comment, setComment] = useState("");
  const [file, setFile] = useState();

  const [punchInTime, setPunchInTime] = useState({
    hours: "",
    minutes: "",
    ampm: "AM",
  });

  const [punchOutTime, setPunchOutTime] = useState({
    hours: "",
    minutes: "",
    ampm: "AM",
  });

  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState("");

  const regularisationPayload = useSelector(
    (state) => state.attendance.regularisationPayload,
  );

  const get24HourTime = (time) => {
    if (!time.hours || !time.minutes) return null;
    let hours = parseInt(time.hours, 10);
    if (time.ampm === "PM" && hours !== 12) hours += 12;
    if (time.ampm === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${time.minutes.padStart(2, "0")}`;
  };

  const isDateDisabled = (year, month, day, calendarNumber) => {
    const date = new Date(year, month, day);

    if (calendarNumber === 1) {
      if (min && date < min) return true;
      if (max && date > max) return true;
    }

    if (calendarNumber === 2 && punchInDateObj) {
      if (date < cal2Min) return true;
      if (date > cal2Max) return true;
    }

    return false;
  };

  const isMonthOutOfRange = (year, month, calendarNumber) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);

    if (calendarNumber === 1) {
      if (min && last < min) return true;
      if (max && first > max) return true;
    }

    if (calendarNumber === 2 && punchInDateObj) {
      if (last < cal2Min) return true;
      if (first > cal2Max) return true;
    }

    return false;
  };

  const handleSelect = (value) => {
    setReason(value);
    setOpenReasonDrop(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setOpenReasonDrop(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (resetFlag !== undefined) {
      resetvalues();
    }
  }, [resetFlag]);

  const resetvalues = () => {
    dispatch(attendanceActions.setRegularisationPayload({}));

    setPunchInTime({ hours: "", minutes: "", ampm: "AM" });
    setPunchOutTime({ hours: "", minutes: "", ampm: "AM" });

    setCal1({
      month: today.getMonth(),
      year: today.getFullYear(),
      selected: null,
    });

    setCal2({
      month: today.getMonth(),
      year: today.getFullYear(),
      selected: null,
    });

    setReason("");
    setComment("");
    setFile();
    setIsError(false);
    setErrorText("");
  };

  useEffect(() => {
    setCal2((prev) => ({
      ...prev,
      month: cal1.month,
      year: cal1.year,
      selected: null,
    }));
  }, [cal1.month, cal1.year]);

  useEffect(() => {
    if (!punchInDateObj) return;

    const selectedOut = cal2?.selected
      ? new Date(cal2.year, cal2.month, cal2.selected)
      : null;

    if (selectedOut && (selectedOut < cal2Min || selectedOut > cal2Max)) {
      setCal2((prev) => ({ ...prev, selected: null }));
    }
  }, [cal1.selected]);

  useEffect(() => {
    const punchInDate = cal1.selected
      ? new Date(cal1.year, cal1.month, cal1.selected)
      : null;

    const punchOutDate = cal2.selected
      ? new Date(cal2.year, cal2.month, cal2.selected)
      : null;

    dispatch(
      attendanceActions.setRegularisationPayload({
        ...regularisationPayload,
        punchInDate,
        punchOutDate,
        punchInTime: get24HourTime(punchInTime),
        punchOutTime: get24HourTime(punchOutTime),
        comment,
        reason,
      }),
    );
  }, [cal1, cal2, punchInTime, punchOutTime, comment, reason]);

  useEffect(() => {
    dispatch(
      attendanceActions.setRegularisationPayload({
        ...regularisationPayload,
        file: file || "",
      }),
    );
  }, [file]);

  const generateCalendar = (
    month,
    year,
    selected,
    setSelected,
    calendarNumber,
  ) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const blanks = Array(firstDay).fill(null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
      <div className="regcalendar-grid">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, index) => (
          <div key={d + index} className="rg-day-name">
            {d}
          </div>
        ))}

        {blanks.map((_, i) => (
          <div key={"b" + i}></div>
        ))}

        {days.map((day) => {
          const disabled =
            isDateDisabled(year, month, day, calendarNumber) ||
            (calendarNumber === 2 && !isPunchInSelected);

          return (
            <div
              key={day}
              className={`rg-date ${selected === day ? "reg-selected" : ""} ${
                disabled ? "reg-disabled" : ""
              }`}
              onClick={() => {
                if (!disabled) setSelected(day);
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    );
  };

  const changeMonth = (calendar, direction) => {
    const setter = calendar === 1 ? setCal1 : setCal2;
    const state = calendar === 1 ? cal1 : cal2;

    let newMonth = state.month + direction;
    let newYear = state.year;

    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }

    if (isMonthOutOfRange(newYear, newMonth, calendar)) return;
    if (calendar === 2 && !isPunchInSelected) return;

    setter({ ...state, month: newMonth, year: newYear });
  };

  const renderCalendar = (
    calendarNumber,
    calState,
    setCalState,
    time,
    setTime,
  ) => {
    const prevMonth = new Date(calState.year, calState.month - 1);
    const nextMonth = new Date(calState.year, calState.month + 1);

    const isCalendarDisabled = calendarNumber === 2 && !isPunchInSelected;

    const isPrevDisabled =
      isCalendarDisabled ||
      isMonthOutOfRange(
        prevMonth.getFullYear(),
        prevMonth.getMonth(),
        calendarNumber,
      );

    const isNextDisabled =
      isCalendarDisabled ||
      isMonthOutOfRange(
        nextMonth.getFullYear(),
        nextMonth.getMonth(),
        calendarNumber,
      );

    return (
      <div className={`regcard ${isCalendarDisabled ? "disabled" : ""}`}>
        <div className="reg-cal-head">
          <img
            src={calendarNumber === 1 ? LoginIcon : LogoutIcon}
            className="reg-cal-head-ico"
            alt=""
          />
          <div className="reg-cal-header">
            {calendarNumber === 1 ? "Punch In" : "Punch Out"} date & time
          </div>
        </div>

        <div className="regcalendar-contanier">
          <div className="regcalendar-header">
            <div className="reg-dropdowns">
              <select
                value={calState.month}
                disabled={isCalendarDisabled}
                onChange={(e) =>
                  setCalState({ ...calState, month: +e.target.value })
                }
              >
                {monthNames.map((m, i) => (
                  <option key={i} value={i}>
                    {m}
                  </option>
                ))}
              </select>

              <select
                value={calState.year}
                disabled={isCalendarDisabled}
                onChange={(e) =>
                  setCalState({ ...calState, year: +e.target.value })
                }
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="regcal-nav">
              <button
                className="regcal-nav-btn"
                disabled={isPrevDisabled}
                onClick={() => changeMonth(calendarNumber, -1)}
              >
                ‹
              </button>

              <button
                className="regcal-nav-btn"
                disabled={isNextDisabled}
                onClick={() => changeMonth(calendarNumber, 1)}
              >
                ›
              </button>
            </div>
          </div>

          {generateCalendar(
            calState.month,
            calState.year,
            calState.selected,
            (day) => setCalState({ ...calState, selected: day }),
            calendarNumber,
          )}
        </div>

        <div className="reg-time-picker">
          <input
            type="number"
            placeholder="HH"
            min="1"
            max="12"
            disabled={isCalendarDisabled}
            value={time.hours}
            // onChange={(e) => setTime({ ...time, hours: e.target.value })}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setTime({ ...time, hours: "" });
                return;
              }
              const numeric = parseInt(value, 10);
              if (Number.isNaN(numeric)) return;
              const hours = Math.min(12, Math.max(1, numeric));
              setTime({ ...time, hours: hours.toString() });
            }}
          />
          <span>:</span>
          <input
            type="number"
            placeholder="MM"
            min="0"
            max="59"
            disabled={isCalendarDisabled}
            value={time.minutes}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setTime({ ...time, minutes: "" });
                return;
              }
              const numeric = parseInt(value, 10);
              if (Number.isNaN(numeric)) return;
              const minutes = Math.min(59, Math.max(0, numeric));
              setTime({
                ...time,
                minutes: minutes.toString().padStart(2, "0"),
              });
            }}
          />
          <select
            value={time.ampm}
            disabled={isCalendarDisabled}
            onChange={(e) => setTime({ ...time, ampm: e.target.value })}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
    );
  };

  const validateAttendanceData = (data) => {
    const { punchInDate, punchOutDate, punchInTime, punchOutTime } = data;
    const isSameDate = isSameDay(new Date(punchInDate), new Date(punchOutDate));
    if (isSameDate) {
      const baseDate = new Date(punchInDate);
      const punchIn = parse(punchInTime, "HH:mm", baseDate);
      const punchOut = parse(punchOutTime, "HH:mm", baseDate);
      const hours = differenceInHours(punchOut, punchIn);
      if (hours > 0) {
        return false;
      }
      return true;
    }

    return false;
  };

  const onRegularisationValidation = () => {
    setIsError(false);
    const mandatoryFields = [
      "punchInDate",
      "punchOutDate",
      "punchInTime",
      "punchOutTime",
      "reason",
    ];
    if (reason === "Others") {
      mandatoryFields.push("comment");
    }

    const missingKey = mandatoryFields?.find((key) => {
      const value = regularisationPayload[key];

      const isInvalidDate =
        (value instanceof Date && isNaN(value.getTime())) ||
        value === "Invalid date";

      return (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "number" && isNaN(value)) ||
        isInvalidDate
      );
    });
    if (missingKey) {
      setIsError(true);
      setErrorText(
        `Missing ${missingKey} field. Please fill all the mandatory fields.`,
      );
      return;
    }
    let validateSubmit = validateAttendanceData(regularisationPayload);
    if (validateSubmit) {
      setIsError(true);
      setErrorText(
        "Punch out time cannot be earlier than Punch in time on the same date.Please check the date and time",
      );
      return;
    }
    const isSameDate = isSameDay(
      new Date(regularisationPayload?.punchInDate),
      new Date(regularisationPayload?.punchOutDate),
    );
    const timeDifference = getTimeDifference(
      regularisationPayload?.punchInTime,
      regularisationPayload?.punchOutTime,
    );
    console.log("time diff", timeDifference);

    if (isSameDate && timeDifference?.hours > 12) {
      setIsError(true);
      setErrorText("Attendance duration cannot be more than 12 hours");
      return;
    } else if (isSameDate && timeDifference.hours < 4) {
      setIsError(true);
      setErrorText("Attendance duration cannot be less than 4 hours");
      return;
    }
    if (!regularisationPayload?.file) {
      setIsError(true);
      setErrorText("Attachment file missing");
      return;
    }
    onSubmitRegularisation();
  };

  return (
    <div className="reg-container">
      <div className="regcalendar-wrapper">
        {renderCalendar(1, cal1, setCal1, punchInTime, setPunchInTime)}
        {renderCalendar(2, cal2, setCal2, punchOutTime, setPunchOutTime)}
      </div>

      {isError && (
        <div className="error-wrapper">
          <span
            className="material-symbols-rounded"
            style={{ fontSize: "16px", color: "red" }}
          >
            info
          </span>
          <div className="half-error-message">{errorText}</div>
        </div>
      )}

      <div className="reg-footer">
        <div className="flex justify-between w-full">
          <div ref={dropdownRef}>
            <div
              className="reg-reason"
              onClick={(e) => {
                e.stopPropagation();
                setOpenReasonDrop((prev) => !prev);
              }}
            >
              {reason
                ? reason === "Others"
                  ? `Others | ${comment}`
                  : reason
                : "Select a reason for regularisation"}
            </div>

            {openReasonDrop && (
              <div className="reg-dropdown-menu">
                <div
                  className="reg-drop-item"
                  onClick={() => handleSelect("Login Issue")}
                >
                  Login Issue
                </div>

                <div
                  className="reg-drop-item"
                  onClick={() => handleSelect("Outdoor Duty")}
                >
                  Outdoor Duty
                </div>

                <div className="other-section">
                  <div>Others</div>

                  <input
                    type="text"
                    placeholder="Enter reason"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      setReason("Others");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setOpenReasonDrop(false);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="reg-attach-file">
            {file ? (
              <div className="flex items-center">
                <div className="reg-attach-file-text">{file?.name}</div>
                <img
                  src={DeleteIcon}
                  className="reg-delete-ico"
                  alt=""
                  onClick={() => {
                    setFile();
                  }}
                />
              </div>
            ) : (
              <>
                <input
                  type="file"
                  className="reg-attach-input"
                  onChange={(e) => setFile(e.target.files[0])}
                />
                <img
                  className="reg-attach-file-icon"
                  src={AttachIcon}
                  alt="Attach File"
                />
                <div className="reg-attach-file">Add Attachment</div>
              </>
            )}
          </div>
        </div>
        <button
          className="reg-submit-btn"
          disabled={
            submitRegularisationResponseStatus === "PENDING" ||
            regularisationFileUploadResponseStatus === "PENDING"
          }
          onClick={onRegularisationValidation}
        >
          {submitRegularisationResponseStatus === "PENDING" ||
          regularisationFileUploadResponseStatus === "PENDING"
            ? "Submitting Regularisation..."
            : "Submit Regularisation"}
        </button>
      </div>
    </div>
  );
};

export default Regularisation;
